/**
 * LeadSourcesPanel — v2.8.0
 *
 * Settings → Sources de leads (admin only). Configure external lead intake
 * webhooks :
 *   - Facebook Lead Ads (Meta App → /api/webhooks/facebook/leadgen)
 *   - Make.com           (any Make scenario → /api/webhooks/make)
 *
 * The panel exposes :
 *   - the public webhook URLs to paste into the third-party platforms (one-click copy)
 *   - the per-source secrets stored in AppSettings (verify_token, app_secret,
 *     page_access_token, form_id mapping, make_webhook_secret)
 *   - a form_id → projet_id mapping table for Facebook (so each Lead Form
 *     can route to a dedicated SOGI project)
 *
 * Secrets are masked by default with a show/hide toggle. The backend writes
 * them straight into MongoDB ; rotating a secret is just an edit + save.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Facebook,
  Zap,
  Copy,
  Eye,
  EyeOff,
  Save,
  Plus,
  Trash2,
  Loader2,
  KeyRound,
  Link as LinkIcon,
  ScrollText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { settingsApi, projectsApi } from "@/lib/crmApi";

// Base URL exposed by Vite/CRA. Falls back to `window.location.origin` so
// the UI shows a sensible URL even in dev where the env var may be unset.
function backendUrl() {
  return (
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== "undefined" ? window.location.origin : "")
  );
}

const FB_WEBHOOK_PATH = "/api/webhooks/facebook/leadgen";
const MAKE_WEBHOOK_PATH = "/api/webhooks/make";

function CopyButton({ value, label = "URL" }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-7 gap-1 text-[11px]"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          toast.success(`${label} copiée`);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error("Échec de la copie");
        }
      }}
      data-testid={`copy-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <Copy className="h-3 w-3" />
      {copied ? "Copié ✓" : "Copier"}
    </Button>
  );
}

function SecretInput({ value, onChange, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pr-9 font-mono text-xs"
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        title={show ? "Masquer" : "Afficher"}
      >
        {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

export default function LeadSourcesPanel({ projetId = null, projetNom = "" }) {
  const [settings, setSettings] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Local form mirrors the persisted settings until the user clicks Save.
  // Empty strings instead of nulls so the controlled inputs don't warn.
  const [fbVerify, setFbVerify] = useState("");
  const [fbAppSecret, setFbAppSecret] = useState("");
  const [fbPageToken, setFbPageToken] = useState("");
  const [fbMapping, setFbMapping] = useState([]); // [{form_id, projet_id}]
  const [makeSecret, setMakeSecret] = useState("");

  const fbUrl = useMemo(() => `${backendUrl()}${FB_WEBHOOK_PATH}`, []);
  const makeUrl = useMemo(() => `${backendUrl()}${MAKE_WEBHOOK_PATH}`, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // App Secret, Verify token, form→projet mapping and the Make secret
        // are GLOBAL (tied to the single Meta app) → read from settingsApi.get().
        // Only the Page Access Token is per-project → read from the project's
        // effective settings (falls back to the global token when unset).
        const [s, p, proj] = await Promise.all([
          settingsApi.get(),
          projectsApi.list(),
          projetId
            ? settingsApi.getForProject(projetId).then((r) => r.effective || {})
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setSettings(s);
        setProjects(Array.isArray(p) ? p : []);
        setFbVerify(s?.facebook_verify_token || "");
        setFbAppSecret(s?.facebook_app_secret || "");
        // Per-project page token when a project is active, else the global one.
        setFbPageToken(
          (proj ? proj.facebook_page_access_token : s?.facebook_page_access_token) || ""
        );
        // form_mapping est stocké en dict côté backend → on transforme en
        // array pour pouvoir éditer ligne par ligne dans l'UI.
        const mapDict = s?.facebook_form_mapping || {};
        setFbMapping(
          Object.entries(mapDict).map(([form_id, projet_id]) => ({
            form_id,
            projet_id,
          }))
        );
        setMakeSecret(s?.make_webhook_secret || "");
      } catch (e) {
        toast.error("Impossible de charger les paramètres");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projetId]);

  const addMappingRow = () =>
    setFbMapping((rows) => [...rows, { form_id: "", projet_id: "" }]);
  const removeMappingRow = (idx) =>
    setFbMapping((rows) => rows.filter((_, i) => i !== idx));
  const updateMappingRow = (idx, key, value) =>
    setFbMapping((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
    );

  const save = async () => {
    setSaving(true);
    try {
      // Re-shape mapping back to dict, dropping empty rows.
      const mappingDict = {};
      for (const r of fbMapping) {
        const fid = (r.form_id || "").trim();
        const pid = (r.projet_id || "").trim();
        if (fid && pid) mappingDict[fid] = pid;
      }
      // Global fields (one Meta app) → app_settings singleton.
      await settingsApi.update({
        facebook_verify_token: fbVerify.trim(),
        facebook_app_secret: fbAppSecret.trim(),
        facebook_form_mapping: mappingDict,
        make_webhook_secret: makeSecret.trim(),
      });
      // Per-project Page Access Token → project_settings override. When no
      // project is active, store it on the global singleton (legacy default).
      if (projetId) {
        await settingsApi.updateForProject(projetId, {
          facebook_page_access_token: fbPageToken.trim(),
        });
      } else {
        await settingsApi.update({
          facebook_page_access_token: fbPageToken.trim(),
        });
      }
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Échec de l'enregistrement"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border/60 bg-card">
        <div className="flex items-start justify-between gap-3 border-b border-border/40 px-4 py-3">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400">
              <Facebook className="h-4 w-4" />
            </span>
            <div>
              <h3 className="font-heading text-sm font-semibold">
                Facebook Lead Ads
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Réception automatique des leads des publicités Facebook /
                Instagram. Chaque Lead Form peut être routé vers un projet
                SOGI différent via le mapping ci-dessous.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-4 py-4">
          {projetNom && (
            <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
              Seul le <strong>Page Access Token</strong> est propre au projet{" "}
              <strong>{projetNom}</strong>. L'<strong>App Secret</strong>, le{" "}
              <strong>Verify token</strong>, le <strong>routage Form → Projet</strong>{" "}
              et le secret Make sont <strong>communs à tous les projets</strong>{" "}
              (une seule app Meta).
            </div>
          )}
          {/* Webhook URL — à coller dans le dashboard Meta */}
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              URL du webhook (à coller dans Meta App Dashboard)
            </Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded border border-border/60 bg-muted/40 px-2 py-1.5 text-[11px]">
                {fbUrl}
              </code>
              <CopyButton value={fbUrl} label="URL Facebook" />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Dans Meta for Developers → App → Webhooks → Page → Subscribe →
              champ <strong>leadgen</strong>.
            </p>
          </div>

          {/* Verify token */}
          <div className="space-y-1.5">
            <Label
              htmlFor="fb-verify"
              className="text-[11px] uppercase tracking-wide text-muted-foreground"
            >
              <KeyRound className="mr-1 inline h-3 w-3" />
              Verify token
            </Label>
            <SecretInput
              value={fbVerify}
              onChange={setFbVerify}
              placeholder="Une chaîne aléatoire — collez la même dans Meta"
              testId="fb-verify-token"
            />
            <p className="text-[11px] text-muted-foreground">
              Sert au handshake initial avec Meta. Cette valeur doit être
              identique des deux côtés.
            </p>
          </div>

          {/* App secret */}
          <div className="space-y-1.5">
            <Label
              htmlFor="fb-secret"
              className="text-[11px] uppercase tracking-wide text-muted-foreground"
            >
              <KeyRound className="mr-1 inline h-3 w-3" />
              App secret (Meta App → Settings → Basic)
            </Label>
            <SecretInput
              value={fbAppSecret}
              onChange={setFbAppSecret}
              placeholder="32-char hex secret"
              testId="fb-app-secret"
            />
            <p className="text-[11px] text-muted-foreground">
              Utilisé pour vérifier la signature HMAC de chaque webhook. Sans
              ce secret, les payloads sont rejetés (401).
            </p>
          </div>

          {/* Page access token */}
          <div className="space-y-1.5">
            <Label
              htmlFor="fb-token"
              className="text-[11px] uppercase tracking-wide text-muted-foreground"
            >
              <KeyRound className="mr-1 inline h-3 w-3" />
              Page Access Token (long-lived)
              {projetNom && (
                <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                  Projet : {projetNom}
                </span>
              )}
            </Label>
            <SecretInput
              value={fbPageToken}
              onChange={setFbPageToken}
              placeholder="EAAB... (généré dans Graph API Explorer)"
              testId="fb-page-token"
            />
            <p className="text-[11px] text-muted-foreground">
              Permet au CRM de récupérer les détails complets de chaque lead
              via Graph API. Token de Page, pas de User.{" "}
              {projetNom
                ? `Spécifique au projet ${projetNom} (chaque projet = sa propre Page).`
                : ""}
            </p>
          </div>

          {/* Form mapping */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Routage Form → Projet
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addMappingRow}
                className="h-7 gap-1 text-[11px]"
                data-testid="fb-mapping-add"
              >
                <Plus className="h-3 w-3" />
                Ajouter une règle
              </Button>
            </div>
            {fbMapping.length === 0 ? (
              <p className="rounded border border-dashed border-border/60 px-3 py-3 text-center text-[11px] text-muted-foreground">
                Aucun mapping. Les leads iront dans le projet actif par
                défaut. Ajoutez une règle pour router un Form spécifique
                vers un projet dédié.
              </p>
            ) : (
              <div className="space-y-2">
                {fbMapping.map((row, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2"
                    data-testid={`fb-mapping-row-${idx}`}
                  >
                    <Input
                      placeholder="form_id Facebook"
                      value={row.form_id}
                      onChange={(e) =>
                        updateMappingRow(idx, "form_id", e.target.value)
                      }
                      className="h-8 flex-1 font-mono text-xs"
                    />
                    <Select
                      value={row.projet_id || ""}
                      onValueChange={(v) =>
                        updateMappingRow(idx, "projet_id", v)
                      }
                    >
                      <SelectTrigger className="h-8 flex-1 text-xs">
                        <SelectValue placeholder="Projet cible" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => removeMappingRow(idx)}
                      className="h-8 w-8 text-muted-foreground hover:text-red-600"
                      title="Supprimer cette règle"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Make.com section */}
      <div className="rounded-xl border border-border/60 bg-card">
        <div className="flex items-start justify-between gap-3 border-b border-border/40 px-4 py-3">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400">
              <Zap className="h-4 w-4" />
            </span>
            <div>
              <h3 className="font-heading text-sm font-semibold">
                Make (anciennement Integromat)
              </h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Endpoint générique pour recevoir des leads depuis n'importe
                quel scénario Make (Google Sheets, Mailchimp, Webflow, etc.).
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-4 py-4">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              URL du webhook
            </Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded border border-border/60 bg-muted/40 px-2 py-1.5 text-[11px]">
                {makeUrl}
              </code>
              <CopyButton value={makeUrl} label="URL Make" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              <KeyRound className="mr-1 inline h-3 w-3" />
              Secret partagé (header <code>X-Make-Secret</code>)
            </Label>
            <SecretInput
              value={makeSecret}
              onChange={setMakeSecret}
              placeholder="Une chaîne aléatoire à coller dans Make"
              testId="make-secret"
            />
            <p className="text-[11px] text-muted-foreground">
              Chaque scénario Make doit envoyer ce secret dans le header
              <code className="mx-1 rounded bg-muted/60 px-1">X-Make-Secret</code>
              sinon la requête est rejetée (401).
            </p>
          </div>

          <details className="rounded border border-border/60 bg-muted/20 text-[11px]">
            <summary className="cursor-pointer px-3 py-2 font-medium">
              Exemple de payload accepté
            </summary>
            <pre className="overflow-auto px-3 py-2 text-[10.5px] leading-snug">
{`{
  "phone": "+221 77 800 12 34",   // requis
  "name": "Mehdi Diop",
  "email": "mehdi@example.com",
  "ville": "Dakar",
  "budget": "50M FCFA",
  "type_appart": "3_pieces",
  "horizon": "1-3mois",
  "programme": "RYAD Almadies",   // optionnel — sinon projet actif
  "notes": "Vu sur Webflow"
}`}
            </pre>
          </details>
        </div>
      </div>

      {/* Bottom save bar (sticky on long page) */}
      <div className="sticky bottom-0 -mx-1 flex items-center justify-end gap-2 border-t border-border/40 bg-card/95 px-4 py-3 backdrop-blur">
        <Button
          onClick={save}
          disabled={saving}
          className="gap-1.5"
          data-testid="lead-sources-save"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" />
          )}
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}
